/**
 * TranscriptTray — Sliding panel for transcript review and editing.
 *
 * Opens from the document editor header (FileText icon).
 * Supports two positions:
 *   • right  — side-by-side with document sections (default)
 *   • bottom — stacked below document sections
 *
 * App Shell only. Gated behind the `advancedTranscript` feature flag
 * for editing / confidence features; read-only view is ungated.
 *
 * See: docs/features/document-system/transcript-improvements.md
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { X, PanelRight, PanelBottom, Copy, Check, BarChart3, Users, Pencil, Save as SaveIcon } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import TranscriptContent from "./TranscriptContent";
import SpeakerRoleAssignment, { buildDefaultAssignments } from "./SpeakerRoleAssignment";
import { getQualityInfo, countLowConfidenceWords } from "./TranscriptContent";
import { auth, db } from "../../utils/firebase";
import { doc, updateDoc } from "firebase/firestore";
import "../../css/transcript-tray.css";

const MIN_BOTTOM_HEIGHT = 150;
const MAX_BOTTOM_HEIGHT_RATIO = 0.6; // 60 % of viewport
const DEFAULT_BOTTOM_HEIGHT = 280;

const TranscriptTray = ({
  isOpen,
  position = "right", // "right" | "bottom"
  onClose,
  onTogglePosition,
  documentData,
  patientId,
  patientName = "",
  userId,
  clinicianDisplayName = "",
  onClinicianNameChange,
  settings,
  advancedTranscript = false,
}) => {
  // ---------- resize (bottom mode) ----------
  const [bottomHeight, setBottomHeight] = useState(DEFAULT_BOTTOM_HEIGHT);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    startY.current = e.clientY;
    startH.current = bottomHeight;

    const onMove = (ev) => {
      if (!isDragging.current) return;
      const delta = startY.current - ev.clientY; // drag up = grow
      const maxH = window.innerHeight * MAX_BOTTOM_HEIGHT_RATIO;
      setBottomHeight(Math.max(MIN_BOTTOM_HEIGHT, Math.min(maxH, startH.current + delta)));
    };
    const onUp = () => {
      isDragging.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [bottomHeight]);

  // ---------- copy transcript ----------
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  // ---------- quality report ----------
  const [showQuality, setShowQuality] = useState(false);
  const [qualityState, setQualityState] = useState(null);

  // ---------- speaker roles ----------
  const [showSpeakerRoles, setShowSpeakerRoles] = useState(false);
  const [speakerRoles, setSpeakerRoles] = useState({});
  
  // ---------- speaker labels toggle (per-document) ----------
  const [hideSpeakerLabels, setHideSpeakerLabels] = useState(true);
  // Gate hydration of hideSpeakerLabels from richData so that after the user
  // toggles it manually, a later onQualityData call (e.g. from a speaker-role
  // save that re-notifies) can't revert the toggle.
  const hideSpeakerLabelsHydrated = useRef(false);

  // ---------- edit mode ----------
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [saveError, setSaveError] = useState("");
  const savingEditsRef = useRef(false);
  const pendingEditsRef = useRef(null); // latest edited utterances from TranscriptContent
  const pendingUtterancesSaveRef = useRef(null);
  const lastSavedUtterancesSignatureRef = useRef(null);
  const isPersistingUtterancesRef = useRef(false);
  const previousDocIdRef = useRef(documentData?.id ?? null);

  const handleQualityData = useCallback(({ richData, qualityData }) => {
    setQualityState({ richData, qualityData });
    // Use saved speaker roles, or auto-assign defaults so transcript
    // renders with clinician/patient names immediately on first load.
    if (richData?.speakerRoles && Object.keys(richData.speakerRoles).length > 0) {
      setSpeakerRoles(richData.speakerRoles);
    } else if (richData?.speakerCount > 0) {
      setSpeakerRoles(buildDefaultAssignments(richData.speakerCount));
    }
    // Load saved speaker labels toggle state — only on first hydration per doc.
    if (!hideSpeakerLabelsHydrated.current && richData?.hideSpeakerLabels !== undefined) {
      setHideSpeakerLabels(richData.hideSpeakerLabels);
      hideSpeakerLabelsHydrated.current = true;
    }
  }, []);

  // Reset hydration flag when the document changes so a new doc re-hydrates.
  useEffect(() => {
    hideSpeakerLabelsHydrated.current = false;
  }, [documentData?.id]);

  const avgConfidence = qualityState?.richData?.avgConfidence ?? qualityState?.qualityData?.avgConfidence ?? null;
  const qualityInfo = avgConfidence != null ? getQualityInfo(avgConfidence) : null;
  const qualityPct = avgConfidence != null ? Math.round(avgConfidence * 100) : null;
  const wordCount = qualityState?.richData?.wordCount ?? null;
  const lowQualityCount = qualityState?.richData?.utterances
    ? countLowConfidenceWords(qualityState.richData.utterances)
    : 0;
  const speakerCount = qualityState?.richData?.speakerCount ?? 0;
  const retentionDeleted = qualityState?.richData?.retentionDeleted === true
    || qualityState?.richData?._metadata?.retentionDeleted === true;

  // Get first utterance per speaker for preview
  const sampleUtterances = React.useMemo(() => {
    if (!qualityState?.richData?.utterances) return [];
    const seen = new Set();
    const samples = [];
    for (const utt of qualityState.richData.utterances) {
      if (utt.speaker !== undefined && !seen.has(utt.speaker)) {
        seen.add(utt.speaker);
        samples.push(utt);
      }
    }
    return samples;
  }, [qualityState?.richData?.utterances]);

  // Real-time role preview (updates state without saving to Firestore)
  const handleRolesPreview = useCallback((newRoles) => {
    setSpeakerRoles(newRoles);
  }, []);

  // Save speaker roles to backend (metadata stays in Firestore, transcript data in GCS)
  const handleRolesChange = useCallback(async (newRoles) => {
    setSpeakerRoles(newRoles);
    if (!patientId || !documentData?.id) return;
    
    try {
      // Save via backend API (updates speakerRoles in both GCS version + Firestore metadata)
      const token = await auth.currentUser?.getIdToken();
      if (token) {
        const resp = await fetch(`/api/transcript-data/${patientId}/${documentData.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ speakerRoles: newRoles }),
        });
        if (!resp.ok) throw new Error(`API save failed: ${resp.status}`);
      } else {
        // Fallback to direct Firestore write (legacy)
        const sessionPath = `patients/${patientId}/documents/${documentData.id}/transcriptionData/session`;
        const docRef = doc(db, sessionPath);
        await updateDoc(docRef, {
          speakerRoles: newRoles,
          speakerRolesUpdatedAt: new Date(),
        });
      }
    } catch (err) {
      console.error("[TranscriptTray] Failed to save speaker roles:", err);
    }
  }, [patientId, documentData?.id]);

  // Toggle speaker labels visibility (per-document setting).
  // Writes through the backend API (same path as role saves) so we get
  // consistent auth, access checks, and GCS-metadata handling.
  const handleToggleSpeakerLabels = useCallback(async () => {
    const newValue = !hideSpeakerLabels;
    setHideSpeakerLabels(newValue);

    if (!patientId || !documentData?.id) return;

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("no auth token");
      const resp = await fetch(`/api/transcript-data/${patientId}/${documentData.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ hideSpeakerLabels: newValue }),
      });
      if (!resp.ok) throw new Error(`API save failed: ${resp.status}`);
    } catch (err) {
      console.error("[TranscriptTray] Failed to save speaker labels toggle:", err);
      // Revert local state so UI reflects the actual persisted value.
      setHideSpeakerLabels(!newValue);
    }
  }, [hideSpeakerLabels, patientId, documentData?.id]);

  // Persist utterances after diarization edits.
  const persistUtterancesChange = useCallback(async (newUtterances) => {
    if (!patientId || !documentData?.id) return;

    try {
      // Save via backend API (writes new GCS version)
      const token = await auth.currentUser?.getIdToken();
      if (token) {
        const resp = await fetch(`/api/transcript-data/${patientId}/${documentData.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ utterances: newUtterances }),
        });
        if (!resp.ok) throw new Error(`API save failed: ${resp.status}`);
      } else {
        // Fallback to direct Firestore write (legacy)
        const sessionPath = `patients/${patientId}/documents/${documentData.id}/transcriptionData/session`;
        const docRef = doc(db, sessionPath);
        await updateDoc(docRef, {
          utterances: newUtterances,
          diarizationEditedAt: new Date(),
          diarizationEditedBy: userId,
        });
      }
    } catch (err) {
      console.error("[TranscriptTray] Failed to save utterance changes:", err);
    }
  }, [patientId, documentData?.id, userId]);

  const flushPendingUtterancesSave = useCallback(async () => {
    if (isPersistingUtterancesRef.current) return;

    const latest = pendingUtterancesSaveRef.current;
    if (!latest) return;

    const signature = JSON.stringify(latest);
    if (signature === lastSavedUtterancesSignatureRef.current) {
      pendingUtterancesSaveRef.current = null;
      return;
    }

    pendingUtterancesSaveRef.current = null;
    isPersistingUtterancesRef.current = true;
    try {
      await persistUtterancesChange(latest);
      lastSavedUtterancesSignatureRef.current = signature;
    } finally {
      isPersistingUtterancesRef.current = false;

      // If new edits landed while persisting, immediately flush the latest.
      if (pendingUtterancesSaveRef.current) {
        flushPendingUtterancesSave();
      }
    }
  }, [persistUtterancesChange]);

  const scheduleUtterancesSave = useCallback((newUtterances) => {
    // Keep only the latest local edit; persistence flushes on close/unmount/doc switch.
    pendingUtterancesSaveRef.current = newUtterances;
  }, []);

  // Save utterances when user adjusts diarization breaks
  const handleUtterancesChange = useCallback((newUtterances) => {
    // Update local quality state immediately so dragging stays responsive.
    setQualityState((prev) => ({
      ...prev,
      richData: prev?.richData ? { ...prev.richData, utterances: newUtterances } : prev?.richData,
    }));
    scheduleUtterancesSave(newUtterances);
  }, [scheduleUtterancesSave]);

  useEffect(() => {
    return () => {
      flushPendingUtterancesSave();
    };
  }, [flushPendingUtterancesSave]);

  useEffect(() => {
    if (!isOpen) {
      flushPendingUtterancesSave();
    }
  }, [isOpen, flushPendingUtterancesSave]);

  useEffect(() => {
    const currentDocId = documentData?.id ?? null;
    if (previousDocIdRef.current !== currentDocId) {
      flushPendingUtterancesSave();
      previousDocIdRef.current = currentDocId;
      // New document should not be deduped against prior doc payload.
      lastSavedUtterancesSignatureRef.current = null;
      pendingUtterancesSaveRef.current = null;
    }
  }, [documentData?.id, flushPendingUtterancesSave]);

  // ---------- text editing ----------
  const handleEditedUtterancesChange = useCallback((next) => {
    pendingEditsRef.current = next;
  }, []);

  const handleStartEdit = useCallback(() => {
    pendingEditsRef.current = null;
    setSaveError("");
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    pendingEditsRef.current = null;
    setSaveError("");
    setIsEditing(false);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (savingEditsRef.current) return;
    const edited = pendingEditsRef.current;
    if (!edited || !patientId || !documentData?.id) {
      // Nothing changed — just exit edit mode.
      setIsEditing(false);
      return;
    }

    savingEditsRef.current = true;
    setIsSavingEdits(true);
    setSaveError("");
    try {
      const transcript = edited
        .map((u) => (u?.transcript || "").trim())
        .filter(Boolean)
        .join(" ");

      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("no auth token");
      const resp = await fetch(`/api/transcript-data/${patientId}/${documentData.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ utterances: edited, transcript }),
      });
      if (!resp.ok) throw new Error(`API save failed: ${resp.status}`);
      const result = await resp.json();
      if (!Number.isInteger(result.version) || result.version < 1) {
        throw new Error("API did not confirm a saved transcript version");
      }

      // Update local quality state so UI reflects the edit without a re-fetch.
      setQualityState((prev) => ({
        ...prev,
        richData: prev?.richData
          ? { ...prev.richData, utterances: edited, transcript }
          : prev?.richData,
      }));
      pendingEditsRef.current = null;
      setIsEditing(false);
    } catch (err) {
      console.error("[TranscriptTray] Failed to save transcript edits:", err);
      setSaveError("Couldn't save transcript. Try again.");
    } finally {
      savingEditsRef.current = false;
      setIsSavingEdits(false);
    }
  }, [patientId, documentData?.id]);

  // Exit edit mode if the tray is closed or the document changes.
  useEffect(() => {
    if (!isOpen) {
      pendingEditsRef.current = null;
      setIsEditing(false);
    }
  }, [isOpen]);
  useEffect(() => {
    pendingEditsRef.current = null;
    setIsEditing(false);
  }, [documentData?.id]);

  // Save on click outside the tray while editing. Clicks inside the tray
  // (including Save/Cancel buttons) go through their own handlers.
  const trayRef = useRef(null);
  useEffect(() => {
    if (!isEditing || saveError) return;
    const handler = (e) => {
      if (trayRef.current && !trayRef.current.contains(e.target)) {
        handleSaveEdit();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isEditing, saveError, handleSaveEdit]);

  const handleCopy = useCallback(() => {
    // Copy only the transcript text, not the quality/confidence header
    const el = contentRef.current?.querySelector(".transcript-utterances, .transcript-plain-text");
    const text = (el || contentRef.current)?.innerText?.trim() || "";
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, []);

  // ---------- close on Escape ----------
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape" && !savingEditsRef.current) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // ---------- render ----------
  if (!isOpen) return null;

  const isRight = position === "right";
  const positionIcon = isRight ? <PanelBottom size={15} /> : <PanelRight size={15} />;
  const positionLabel = isRight ? "Move to bottom" : "Move to right";

  const trayStyle = !isRight ? { height: `${bottomHeight}px` } : undefined;

  return (
    <div
      ref={trayRef}
      className={`transcript-tray userback-ignore position-${position}`}
      style={trayStyle}
      role="complementary"
      aria-label="Transcript panel"
    >
      {/* Resize handle (bottom mode only) */}
      {!isRight && (
        <div
          className="transcript-tray-resize-handle"
          onMouseDown={handleDragStart}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize transcript panel"
        />
      )}

      {/* Header */}
      <div className="transcript-tray-header">
        <span className="transcript-tray-title">Transcript</span>

        <div className="transcript-tray-actions">
          {advancedTranscript && speakerCount > 0 && !isEditing && (
            <Tooltip title={showSpeakerRoles ? "Hide speaker roles" : "Assign speaker roles"} arrow>
              <button
                className={`transcript-tray-btn${showSpeakerRoles ? " transcript-quality-btn-active" : ""}`}
                onClick={() => setShowSpeakerRoles((p) => !p)}
                aria-label="Toggle speaker roles"
              >
                <Users size={15} />
              </button>
            </Tooltip>
          )}

          {advancedTranscript && qualityInfo && !isEditing && (
            <Tooltip title={showQuality ? "Hide quality report" : "Show quality report"} arrow>
              <button
                className={`transcript-tray-btn${showQuality ? " transcript-quality-btn-active" : ""}`}
                onClick={() => setShowQuality((p) => !p)}
                aria-label="Toggle quality report"
              >
                <BarChart3 size={15} />
              </button>
            </Tooltip>
          )}

          {advancedTranscript && speakerCount > 0 && !isEditing && (
            <Tooltip title="Edit transcript" arrow>
              <button
                className="transcript-tray-btn"
                onClick={handleStartEdit}
                aria-label="Edit transcript"
              >
                <Pencil size={15} />
              </button>
            </Tooltip>
          )}

          {isEditing && (
            <>
              <Tooltip title={isSavingEdits ? "Saving…" : "Save edits"} arrow>
                <span>
                <button
                  className="transcript-tray-btn transcript-quality-btn-active"
                  onClick={handleSaveEdit}
                  disabled={isSavingEdits}
                  aria-label="Save transcript edits"
                >
                  <SaveIcon size={15} />
                </button>
                </span>
              </Tooltip>
              <Tooltip title="Cancel edits" arrow>
                <span>
                <button
                  className="transcript-tray-btn"
                  onClick={handleCancelEdit}
                  disabled={isSavingEdits}
                  aria-label="Cancel transcript edits"
                >
                  <X size={15} />
                </button>
                </span>
              </Tooltip>
            </>
          )}

          {!isEditing && !retentionDeleted && (
            <Tooltip title={copied ? "Copied!" : "Copy transcript"} arrow>
              <button className="transcript-tray-btn" onClick={handleCopy} aria-label="Copy transcript">
                {copied ? <Check size={15} /> : <Copy size={15} />}
              </button>
            </Tooltip>
          )}

          <Tooltip title={positionLabel} arrow>
            <button className="transcript-tray-btn" onClick={onTogglePosition} aria-label={positionLabel}>
              {positionIcon}
            </button>
          </Tooltip>

          <Tooltip title="Close transcript" arrow>
            <span>
            <button className="transcript-tray-btn" onClick={onClose} disabled={isSavingEdits} aria-label="Close transcript panel">
              <X size={15} />
            </button>
            </span>
          </Tooltip>
        </div>
      </div>

      {isSavingEdits && <div className="transcript-save-feedback" role="status">Saving transcript…</div>}
      {saveError && isEditing && (
        <div className="transcript-save-feedback" role="alert">
          <span>{saveError}</span>
          <button type="button" onClick={handleSaveEdit}>Retry</button>
        </div>
      )}

      {/* Speaker role assignment panel */}
      {advancedTranscript && showSpeakerRoles && speakerCount > 0 && (
        <SpeakerRoleAssignment
          speakerCount={speakerCount}
          speakerRoles={speakerRoles}
          patientName={patientName}
          clinicianDisplayName={clinicianDisplayName}
          userId={userId}
          onRolesChange={handleRolesChange}
          onRolesPreview={handleRolesPreview}
          onClinicianNameChange={onClinicianNameChange}
          sampleUtterances={sampleUtterances}
          hideSpeakerLabels={hideSpeakerLabels}
          onToggleSpeakerLabels={handleToggleSpeakerLabels}
        />
      )}

      {/* Quality report panel */}
      {advancedTranscript && showQuality && qualityInfo && (
        <div className="transcript-quality-panel">
          <div className="transcript-quality-panel-row">
            <span className="transcript-quality-panel-label">Transcript Quality</span>
            <span className={`transcript-quality-panel-value ${qualityInfo.className}`}>{qualityInfo.label}</span>
          </div>
          <div className="transcript-quality-panel-row">
            <span className="transcript-quality-panel-label">Total Words</span>
            <span className="transcript-quality-panel-value">{wordCount?.toLocaleString() ?? "—"}</span>
          </div>
          <div className="transcript-quality-panel-row">
            <span className="transcript-quality-panel-label">Low Quality Words</span>
            <span className={`transcript-quality-panel-value${lowQualityCount > 0 ? " quality-poor" : ""}`}>{lowQualityCount.toLocaleString()}</span>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="transcript-tray-body userback-ignore" ref={contentRef}>
        <TranscriptContent
          documentData={documentData}
          patientId={patientId}
          patientName={patientName}
          clinicianDisplayName={clinicianDisplayName}
          speakerRoles={speakerRoles}
          hideSpeakerLabels={hideSpeakerLabels}
          settings={settings}
          advancedTranscript={advancedTranscript}
          isEditing={isEditing}
          isSavingEdits={isSavingEdits}
          onEditedUtterancesChange={handleEditedUtterancesChange}
          onQualityData={handleQualityData}
          onUtterancesChange={handleUtterancesChange}
          utterancesOverride={qualityState?.richData?.utterances || null}
        />
      </div>
    </div>
  );
};

export default React.memo(TranscriptTray);
